import type { ObjectWithObjectID } from '@algolia/client-search';
import algoliasearch from 'algoliasearch';
import invariant from 'invariant';
import queryString from 'query-string';

import { configValue } from 'config/appConfigUtils';
import { RichError } from 'utils/RichError';
import { getJson } from 'utils/http/getJson';
import { trackError } from 'utils/trackError';

import {
  AlgoliaValidUntilError,
  isValidUntilError,
} from './AlgoliaValidUntilError';

type Args = {
  config: Readonly<{ appId: string; apiKey: string }>;
  index: string;
  ids: Array<string>;
  attributes: Array<string>;
};

export async function algoliaGetObjects<T>({
  config: { appId, apiKey },
  index,
  ids,
  attributes,
}: Args): Promise<((T & ObjectWithObjectID) | null)[]> {
  if (configValue('algolia', 'mock')) {
    const idsParams = ids.map((id) => `object_id=${id}`).join('&');
    const url = `http://algoliamock:6577/${index}/objects?${idsParams}`;
    return getJson<{ results: ((T & ObjectWithObjectID) | null)[] }>(url).then(
      (response) => response.results,
    );
  }
  invariant(appId, 'Search app id must be configured');
  invariant(apiKey, 'Search api key must be configured');
  const client = algoliasearch(appId, apiKey);
  const indexClient = client.initIndex(index);

  try {
    const { results } = await indexClient.getObjects<T>(ids, {
      attributesToRetrieve: attributes,
    });

    return results;
    // TODO: fix types
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (e: any) {
    if (isValidUntilError(e)) {
      throw new AlgoliaValidUntilError();
    }

    trackError(
      new RichError('Algolia POST request error.', {
        debugData: JSON.stringify(e.debugData),
        errorMessage: e.message,
        requestParams: queryString.stringify({
          ids,
          attributes,
        }),
        statusCode: e.statusCode,
        response: e.response,
      }),
    );

    return [];
  }
}
